<template>
    <div class="relative cursor-pointer">
        <div class="selected-option relative" @click="toggleDropdown">
            <slot></slot>
            <Tooltip :text="selected?.title" v-if="showTooltip" placement="bottom" />
        </div>

        <div v-if="isOpen" class="ds-radio-menu-wrap" v-click-outside="toggleDropdown">
            <div ref="listRef" class="ds-glass-menu ds-radio-menu" role="menu" @scroll="hideHint">
                <div v-if="title" class="ds-glass-menu-heading">{{ title }}</div>
                <button v-for="option in options" :key="option.value"
                    type="button"
                    role="menuitemradio"
                    class="ds-glass-menu-item"
                    :class="{ 'is-selected': selected?.value === option.value }"
                    :aria-checked="selected?.value === option.value"
                    :aria-describedby="hint?.value === option.value ? hintId : undefined"
                    @mouseenter="scheduleHint(option, $event)"
                    @mouseleave="hideHint"
                    @focus="scheduleHint(option, $event)"
                    @blur="hideHint"
                    @click="selectOption(option)">
                    <span class="ds-glass-menu-item-label">{{ option.title }}</span>
                    <svg v-if="selected?.value === option.value" class="flex-none" width="14" height="14" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <path d="M5 12l5 5L20 7" />
                    </svg>
                </button>
            </div>
            <!-- Описание — соседний элемент списка: не обрезается его прокруткой. Без description не создаётся. -->
            <div v-if="hint" :id="hintId" role="tooltip" class="ds-glass-menu ds-radio-menu-hint" :style="{ top: `${hint.top}px` }">
                {{ hint.text }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue'
import Tooltip from './Tooltip.vue'

interface Option {
    value: string;
    title: string;
    description?: string;
}

interface Props {
    options: Option[],
    showTooltip?: boolean,
    title?: string
}

const props = defineProps<Props>()
const selected = defineModel<Option>()
const emit = defineEmits(['change'])

selected.value = props.options[0]
const isOpen = ref(false)
const listRef = ref<HTMLElement | null>(null)
const hint = ref<{ value: string, text: string, top: number } | null>(null)
const hintId = `dropdown-radio-hint-${Math.random().toString(36).slice(2, 10)}`
const HINT_DELAY = 300
let hintTimer: ReturnType<typeof setTimeout> | undefined

const clearHintTimer = () => {
    if (hintTimer) clearTimeout(hintTimer)
    hintTimer = undefined
}

const hideHint = () => {
    clearHintTimer()
    hint.value = null
}

const scheduleHint = (option: Option, event: Event) => {
    hideHint()
    if (!option.description) return
    const item = event.currentTarget as HTMLElement
    hintTimer = setTimeout(() => {
        hintTimer = undefined
        const list = listRef.value
        if (!list) return
        hint.value = { value: option.value, text: option.description!, top: item.offsetTop - list.scrollTop }
    }, HINT_DELAY)
}

const toggleDropdown = () => {
    hideHint()
    isOpen.value = !isOpen.value
};

const selectOption = (option: Option) => {
    hideHint()
    selected.value = option
    isOpen.value = false
    emit('change', option)
};

onBeforeUnmount(clearHintTimer)
</script>

<style scoped>
.ds-radio-menu-wrap {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    z-index: 20;
}

.ds-radio-menu {
    position: relative;
    min-width: 240px;
    max-width: 380px;
    width: max-content;
    max-height: 384px;
    overflow-y: auto;
}

.ds-radio-menu-hint {
    position: absolute;
    left: calc(100% + 12px);
    width: 250px;
    padding: 10px 12px;
    font-family: var(--font-family-base), sans-serif;
    font-size: 12px;
    line-height: 140%;
    color: var(--color-text-main);
    white-space: pre-line;
    pointer-events: none;
}
</style>
